/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useEffect, useRef} from 'react';

import {VIDEO_AUTOPILOT_BEHAVIORS} from '@amblique/base/app/constants/video-options';
import {videoPilot} from '@amblique/base/app/plugins/video/video-pilot';

/**
 * React hook for video autopilot (intersection-based playback)
 *
 * Automatically controls video playback based on visibility using IntersectionObserver.
 * Implements behaviors like autoplay, resume, and autopause.
 *
 * @param {Object} params
 * @param {HTMLElement} params.elementRef - Video container ref
 * @param {AbstractVideo} params.video - Video instance
 * @param {Object} params.options - Video options
 * @param {Function} params.init - Init function for lazy loading
 * @param {{current: boolean}|undefined} params.suppressLeaveRef - When true, skip autopause on intersection dips (e.g. Swiper touch/transition)
 */
export function useVideoAutopilot({elementRef, video, options, init, suppressLeaveRef}) {
  const observerRef = useRef(null);
  const autopilotRef = useRef(null);

  /**
   * Resolve autopilot behavior configuration
   */
  useEffect(() => {
    if (!options?.autopilot) {
      autopilotRef.current = null;

      return;
    }

    const autopilot = options.autopilot;

    // Boolean: use default behavior
    if (autopilot === true) {
      autopilotRef.current = VIDEO_AUTOPILOT_BEHAVIORS.default;

      return;
    }

    // String: lookup named behavior
    if (typeof autopilot === 'string') {
      autopilotRef.current = VIDEO_AUTOPILOT_BEHAVIORS[autopilot] || VIDEO_AUTOPILOT_BEHAVIORS.default;

      return;
    }

    // Object: custom behavior
    if (typeof autopilot === 'object') {
      autopilotRef.current = {
        ...VIDEO_AUTOPILOT_BEHAVIORS.default,
        ...autopilot,
      };

      return;
    }

    autopilotRef.current = null;
  }, [options]);

  /**
   * Setup IntersectionObserver
   */
  useEffect(() => {
    const autopilot = autopilotRef.current;

    if (!autopilot || (!autopilot.enter && !autopilot.leave)) {
      return;
    }

    const threshold = autopilot.threshold ?? 0.25;
    const rootMargin = autopilot.rootMargin ?? '0px';
    let visible = false;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const nowVisible = entry.intersectionRatio >= threshold;

        if (!visible && nowVisible) {
          if (autopilot.enter) {
            videoPilot.enter({video, init, behaviors: autopilot.enter});
          }
        } else if (visible && !nowVisible && autopilot.leave) {
          if (suppressLeaveRef?.current) {
            // Swiper clips IO during drag; skip pause but sync visible for a later enter().
            visible = nowVisible;

            return;
          }

          videoPilot.leave({video, behaviors: autopilot.leave});
        }

        visible = nowVisible;
      });
    }, {threshold, rootMargin});

    const el = elementRef.current;

    if (el) {
      observer.observe(el);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [video, init, elementRef, options, suppressLeaveRef]);
}
